<script>
  import { apiFetch } from '../lib/auth.js';
  import { onMount } from 'svelte';

  // ── State ──────────────────────────────────────────────────────────
  let articles    = [];
  let listLoad    = false;
  let listError   = '';

  let expandedId    = null;
  let selectedArticle = null;
  let articleLoad   = false;

  let formOpen    = false;
  let editingId   = null;
  let formLoad    = false;
  let formError   = '';
  let formOk      = false;
  let form        = blankForm();

  let deleteConfirm = null;
  let deleteLoad    = false;
  let deleteError   = '';

  function blankForm() {
    return { headline: '', body: '', article_image_url: '', is_published: false };
  }

  // ── Load ───────────────────────────────────────────────────────────
  onMount(loadArticles);

  async function loadArticles() {
    if (listLoad || articles.length > 0) return;
    listLoad = true; listError = '';
    try {
      const data = await apiFetch('/news/all');
      articles = Array.isArray(data) ? data : [];
    } catch (e) {
      listError = e.message || 'Failed to load';
    } finally {
      listLoad = false;
    }
  }

  // ── Select / expand ────────────────────────────────────────────────
  async function selectArticle(id) {
    if (expandedId === id) { expandedId = null; selectedArticle = null; return; }
    expandedId = id; selectedArticle = null; articleLoad = true;
    try {
      selectedArticle = await apiFetch(`/news/${id}`);
    } catch {
      selectedArticle = null;
    } finally {
      articleLoad = false;
    }
  }

  // ── Form helpers ───────────────────────────────────────────────────
  function openNew() {
    editingId = null; form = blankForm(); formError = ''; formOk = false; formOpen = true;
    setTimeout(() => document.querySelector('.news-form-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  async function openEdit(article) {
    formError = ''; formOk = false;
    try {
      const full = await apiFetch(`/news/${article.id}`);
      editingId = full.id;
      form = {
        headline:          full.headline          || '',
        body:              full.body              || '',
        article_image_url: full.article_image_url || '',
        is_published:      full.is_published      === true,
      };
    } catch (e) {
      formError = e.message || 'Failed to load article'; return;
    }
    formOpen = true;
    setTimeout(() => document.querySelector('.news-form-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function closeForm() {
    formOpen = false; editingId = null; formError = '';
  }

  async function submitForm() {
    if (!form.headline.trim()) { formError = 'Headline is required'; return; }
    if (!form.body.trim())     { formError = 'Body is required'; return; }
    formLoad = true; formError = '';
    const payload = {
      headline:          form.headline.trim(),
      body:              form.body.trim(),
      is_published:      form.is_published,
      article_image_url: form.article_image_url.trim() || null,
    };
    try {
      if (editingId) {
        await apiFetch(`/news/${editingId}`, { method: 'PUT', body: JSON.stringify(payload) });
        articles = articles.map(a => a.id === editingId ? { ...a, ...payload } : a);
        if (expandedId === editingId && selectedArticle) selectedArticle = { ...selectedArticle, ...payload };
      } else {
        await apiFetch('/news', { method: 'POST', body: JSON.stringify(payload) });
        articles = [];
        await loadArticles();
      }
      formOk = true;
      setTimeout(() => { formOk = false; closeForm(); }, 900);
    } catch (e) {
      formError = e.message || 'Failed to save';
    } finally {
      formLoad = false;
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────
  async function deleteArticle(id) {
    deleteLoad = true; deleteError = '';
    try {
      await apiFetch(`/news/${id}`, { method: 'DELETE' });
      articles = articles.filter(a => a.id !== id);
      if (expandedId === id)  { expandedId = null; selectedArticle = null; }
      if (editingId  === id)  closeForm();
      deleteConfirm = null;
    } catch (e) {
      deleteError = e.message || 'Failed to delete';
    } finally {
      deleteLoad = false;
    }
  }

  function formatDateTime(str) {
    if (!str) return '—';
    return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
</script>

<!-- ── List header ──────────────────────────────────────────────────── -->
<div class="list-header">
  <span class="record-count">
    {#if listLoad}…{:else}{articles.length} articles{/if}
  </span>
  <div class="list-header-actions">
    <button class="btn-add-top" on:click={openNew}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
      New Article
    </button>
  </div>
</div>

{#if listLoad}
  <div class="state-msg"><span class="spinner"></span> Loading...</div>
{:else if listError}
  <div class="state-error">{listError}</div>
{:else if articles.length === 0 && !formOpen}
  <div class="state-msg empty">No articles yet.</div>
{/if}

<!-- ── Article list ──────────────────────────────────────────────────── -->
{#if articles.length > 0}
  <div class="list">
    {#each articles as article (article.id)}
      <div class="user-block" class:expanded={expandedId === article.id}>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
          class="list-row user-row"
          role="button" tabindex="0"
          on:click={() => selectArticle(article.id)}
          on:keydown={e => e.key === 'Enter' && selectArticle(article.id)}
        >
          <div class="user-summary">
            <span class="user-name">{article.headline}</span>
            <span class="user-email">{article.username} · {formatDateTime(article.created_at)}</span>
          </div>
          <div class="user-meta">
            {#if !article.is_published}
              <span class="status-tag draft">draft</span>
            {:else}
              <span class="status-tag published">published</span>
            {/if}
          </div>
          <svg
            class="row-chevron"
            class:rotated={expandedId === article.id}
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>

        {#if expandedId === article.id}
          <div class="user-detail">
            {#if articleLoad}
              <div class="state-msg"><span class="spinner"></span> Loading...</div>
            {:else if selectedArticle}
              {#if selectedArticle.article_image_url}
                <img
                  src={selectedArticle.article_image_url}
                  alt={selectedArticle.headline}
                  class="news-preview-img"
                />
              {/if}
              <p class="news-preview-body">{selectedArticle.body}</p>
            {/if}

            <div class="article-actions">
              <button class="btn-save-user" on:click={() => openEdit(article)}>Edit</button>

              {#if deleteConfirm === article.id}
                <span class="delete-confirm-label">Delete this article?</span>
                <button class="btn-save-user btn-delete" disabled={deleteLoad}
                  on:click={() => deleteArticle(article.id)}>
                  {deleteLoad ? 'Deleting…' : 'Yes, delete'}
                </button>
                <button class="btn-secondary-sm" on:click={() => deleteConfirm = null}>Cancel</button>
              {:else}
                <button class="btn-save-user btn-delete"
                  on:click={() => { deleteConfirm = article.id; deleteError = ''; }}>Delete</button>
              {/if}

              {#if deleteError}
                <span class="delete-error">{deleteError}</span>
              {/if}
            </div>
          </div>
        {/if}
      </div>
    {/each}
  </div>
{/if}

<!-- ── Create / Edit form ────────────────────────────────────────────── -->
{#if formOpen}
  <div class="form-panel news-form-panel">
    <div class="form-header">
      <span>{editingId ? 'Edit Article' : 'New Article'}</span>
      <button class="btn-dismiss" on:click={closeForm}>✕</button>
    </div>

    {#if formOk}
      <div class="form-success">✓ {editingId ? 'UPDATED' : 'SAVED'}</div>
    {:else}
      {#if formError}<div class="form-error">{formError}</div>{/if}

      <div class="field">
        <label for="news-headline">
          Headline *
          <span class="field-hint" style="display:inline; margin:0 0 0 0.5rem;">({form.headline.length}/60)</span>
        </label>
        <input id="news-headline" type="text" maxlength="60" placeholder="Article headline…" bind:value={form.headline} />
      </div>

      <div class="field">
        <label for="news-body">
          Body *
          <span class="field-hint" style="display:inline; margin:0 0 0 0.5rem;">({form.body.length}/5000)</span>
        </label>
        <textarea id="news-body" maxlength="5000" placeholder="Article body…" bind:value={form.body} rows="8"></textarea>
      </div>

      <div class="field">
        <label for="news-image-url">
          Image URL <span class="field-hint" style="display:inline; margin:0 0 0 0.5rem;">(optional)</span>
        </label>
        <input id="news-image-url" type="text" maxlength="250" placeholder="https://…" bind:value={form.article_image_url} />
      </div>

      <div class="field" style="flex-direction:row; align-items:center; gap:0.75rem; margin-bottom:1rem;">
        <span class="field-label" style="margin:0;">Published</span>
        <button
          class="option-btn"
          class:selected={form.is_published}
          on:click={() => form.is_published = !form.is_published}
        >{form.is_published ? '✓ Live' : 'Draft'}</button>
      </div>

      <div class="btn-row">
        <button class="btn-secondary" on:click={closeForm}>Cancel</button>
        <button class="btn-primary" disabled={formLoad} on:click={submitForm}>
          {formLoad ? 'SAVING...' : editingId ? 'SAVE CHANGES' : 'CREATE ARTICLE'}
        </button>
      </div>
    {/if}
  </div>
{/if}

<style>
  /* Reuse user-block pattern for articles */
  .user-block { border-bottom: 1px solid #0f1a00; }
  .user-block:last-child { border-bottom: none; }
  .user-block.expanded { background: #050b00; }
  .user-row { border-bottom: none !important; }
  .user-summary { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.1rem; }
  .user-name { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.9rem; color: #c8c8c8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .user-email { font-family: 'Rajdhani', sans-serif; font-size: 0.65rem; color: #2a5200; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .user-meta { display: flex; align-items: center; gap: 0.35rem; flex-wrap: wrap; justify-content: flex-end; }

  .status-tag {
    font-family: 'Rajdhani', sans-serif; font-size: 0.48rem; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase; border-radius: 2px; padding: 0.1rem 0.35rem;
  }
  .status-tag.draft     { color: #cc8833; border: 1px solid rgba(180,100,0,0.4); }
  .status-tag.published { color: #6db800; border: 1px solid rgba(100,180,0,0.4); }

  .user-detail { padding: 0.75rem 1rem 1rem 1rem; border-top: 1px solid #0f1a00; }

  .news-preview-img {
    width: 100%; max-height: 180px; object-fit: cover;
    border-radius: 2px; margin-bottom: 0.75rem; border: 1px solid #1e3d00;
  }
  .news-preview-body {
    font-family: 'Rajdhani', sans-serif; font-size: 0.8rem; color: #888;
    line-height: 1.5; margin: 0 0 0.75rem 0; white-space: pre-wrap;
  }

  .article-actions { display: flex; flex-direction: row; gap: 0.5rem; align-items: center; }

  .btn-save-user {
    background: #1a3300; border: 1px solid #3a6b00; color: #6db800;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.55rem;
    letter-spacing: 0.2em; text-transform: uppercase; padding: 0.45rem 0.85rem;
    border-radius: 2px; cursor: pointer; transition: background 0.15s;
  }
  .btn-save-user:hover { background: #223d00; }
  .btn-save-user:disabled { opacity: 0.4; cursor: not-allowed; }
  .btn-delete { background: rgba(100,20,0,0.3); border-color: rgba(180,60,0,0.4); color: #cc6633; }
  .btn-delete:hover { background: rgba(140,30,0,0.4); }

  .btn-secondary-sm {
    background: transparent; border: 1px solid #1e3d00; color: #444;
    font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 0.55rem;
    letter-spacing: 0.15em; text-transform: uppercase; padding: 0.35rem 0.7rem;
    cursor: pointer; border-radius: 2px; transition: border-color 0.2s, color 0.2s;
  }
  .btn-secondary-sm:hover { border-color: #3a6b00; color: #6db800; }

  .delete-confirm-label { font-family: 'Rajdhani', sans-serif; font-size: 0.6rem; color: #cc8833; letter-spacing: 0.1em; }
  .delete-error { font-family: 'Rajdhani', sans-serif; font-size: 0.6rem; color: #cc3300; }

  .news-form-panel { margin-top: 1rem; }
</style>
